/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/horiozontal-block.tsx (non-ecommerce, reason in screen-map §10)
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { PiArrowLeftBold as ArrowLeft } from "react-icons/pi";
import { can } from "../../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../../types";
import { CaveatBanner, PageHeader, WidgetCard, ErrorSurface } from "@steelyard/theme";
import { Alert, Text, Button } from "rizzui";
import { rotateAgentKey, type RotateKeyResult } from "../../../lib/services/tallySync";

/**
 * Ported from apps/admin/app/(app)/integrations/tally/rotate-key/page.tsx, with the rotation
 * action wired up (T012) — the original left it unwired because the response's shape wasn't
 * readable from the generated client; parseRotateKeyResult in lib/services/tallySync.ts closes
 * that gap by runtime-parsing the body instead.
 */
export default function RotateAgentKeyPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [rotating, setRotating] = useState(false);
  const [result, setResult] = useState<RotateKeyResult | null>(null);
  const [rotateError, setRotateError] = useState<unknown>(null);

  async function handleRotate() {
    setRotating(true);
    setRotateError(null);
    try {
      setResult(await rotateAgentKey());
    } catch (err) {
      setRotateError(err);
    } finally {
      setRotating(false);
    }
  }

  if (!can(permissionState, "integrations.administer")) {
    return (
      <ErrorSurface
        error={{
          title: "Not permitted",
          status: 403,
          detail: "Rotating the agent key stops the connector agent working until it is reconfigured with the new one. It is gated separately from reading the integration for that reason.",
        }}
        action="rotate the agent key"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/integrations/tally">
        <Button as="span" size="sm" variant="outline">
          <ArrowLeft aria-hidden className="size-3.5" />
          Back to agent health
        </Button>
      </Link>

      <PageHeader title="Rotate The Agent Key" breadcrumb={[{ name: "Home", href: "/" }, { name: "Integrations" }, { name: "Tally Agent", href: "/integrations/tally" }, { name: "Rotate The Agent Key" }]} />
      <Text className="mb-6 max-w-2xl text-gray-500">
        Read this before rotating. The consequence lands on a machine in the client&rsquo;s office, not on this
        screen.
      </Text>

      <div role="alert">
        <CaveatBanner caveat="The connector agent stops working the moment a new key is issued.">
          <ul className="text-muted-foreground flex list-disc flex-col gap-1 pl-5 text-sm">
            <li>
              The agent must be reconfigured with the new key on the client&rsquo;s Tally machine. Until it is, it
              will authenticate and be refused.
            </li>
            <li>
              <strong>The key is shown once</strong> and never again — the portal stores only a hash. Losing it means
              rotating again.
            </li>
            <li>
              Queued work is <strong>not</strong> lost. A rotation is not a provider change: invoices and credit
              notes stay in the queue and post once the agent is back.
            </li>
            <li>
              A botched rotation shows up as <span className="font-mono">auth_failing</span> on the agent screen
              within minutes, rather than at month end.
            </li>
          </ul>
        </CaveatBanner>
      </div>

      {rotateError ? <ErrorSurface error={rotateError} action="rotate the agent key" /> : null}

      {result ? (
        <Alert color="warning" variant="flat">
          <div role="status" className="flex flex-col gap-1">
            <p className="font-semibold">This is the only time this key is shown. Copy it now and reconfigure the agent before doing anything else.</p>
            <Text className="font-mono text-sm break-all">{result.agentKey}</Text>
          </div>
        </Alert>
      ) : null}

      <WidgetCard title="Rotate the agent key" headerClassName="border-muted border-b">
        <div className="pt-4">
          <Button type="button" onClick={handleRotate} disabled={rotating}>
            {rotating ? "Rotating…" : result ? "Rotate again" : "Rotate the agent key now"}
          </Button>
        </div>
      </WidgetCard>
    </div>
  );
}
